import { DateInput } from '@/common/components/DateInput/DateInput'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useExtendGovernmentAllocation } from '@/features/privateCompanies/hooks/useExtendGovernmentAllocation'
import { GovernmentAllocationInfoItem } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationInfoItem'
import {
  extendGovernmentAllocationSchema,
  ExtendGovernmentAllocationSchema,
} from '@/features/privateCompanies/schemas/extendGovernmentAllocation.schema'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ExtendGovernmentAllocationModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  companyGovernmentAllocation: CompanyGovernmentAllocationData
}

export const ExtendGovernmentAllocationModal = ({
  isOpen,
  setIsOpen,
  companyGovernmentAllocation,
}: ExtendGovernmentAllocationModalProps) => {
  const { companyId, id: governmentAllocationId } = useParams()
  const queryClient = useQueryClient()

  const { getUserPermission } = useUserPermissions()

  const permittedBypassLimitValidation =
    getUserPermission('Admin::Billing::GovernmentAllocations::Extend', true)?.properties
      ?.permitted_bypass_limit_validation === true

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    setError,
    reset: resetForm,
    formState: { errors },
  } = useForm<ExtendGovernmentAllocationSchema>({
    resolver: zodResolver(extendGovernmentAllocationSchema),
    defaultValues: {
      requested_new_end_date: undefined,
    },
  })

  const onSubmit: SubmitHandler<ExtendGovernmentAllocationSchema> = (data) => {
    extendGovernmentAllocation(data)
  }

  const onSuccess = () => {
    resetForm()
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocations', companyId] })
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation', governmentAllocationId] })
    setIsOpen(false)
  }

  const {
    mutate: extendGovernmentAllocation,
    isPending: extendGovernmentAllocationIsPending,
    isError: extendGovernmentAllocationIsError,
  } = useExtendGovernmentAllocation({
    governmentAllocationId,
    onSuccess,
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    },
  })

  return (
    <>
      <Modal
        isOpen={isOpen && !messages}
        setIsOpen={(value) => {
          if (!value) {
            setTimeout(() => resetForm({ requested_new_end_date: undefined }), 300)
          }

          setIsOpen(value)
        }}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={extendGovernmentAllocationIsPending}
        disabled={extendGovernmentAllocationIsPending}
        title="Estender período do empenho"
        footer={`${extendGovernmentAllocationIsPending ? 'Estendendo' : 'Estender'} período de empenho`}
        body={
          <div className="flex flex-col gap-y-4 text-white-800">
            <div className="flex gap-x-2 items-center">
              <InfoIcon strokeWidth={1} className="text-warning" />

              <div>
                <p className="font-bold text-[0.625rem] leading-3">
                  {permittedBypassLimitValidation
                    ? 'Você tem permissão para estender o período para qualquer data deste ano.'
                    : 'Você pode estender o período do empenho em até 25% do período atual.'}
                </p>
              </div>
            </div>

            <GovernmentAllocationInfoItem
              label="Período de competência"
              value={`${dateTransformer(companyGovernmentAllocation.applicable_start_date)} até ${dateTransformer(
                companyGovernmentAllocation.applicable_end_date,
              )}`}
            />

            <form className="flex flex-col">
              <DateInput
                fieldName="requested_new_end_date"
                control={control}
                errors={errors}
                label="Nova data final do período de competência"
                markAsRequired
                disabled={extendGovernmentAllocationIsPending}
              />
            </form>
          </div>
        }
      />

      {messages && (
        <ModalMessageSimple messages={messages} setMessages={setMessages} isError={extendGovernmentAllocationIsError} />
      )}
    </>
  )
}
